<script setup>
import { reactive, computed, ref } from 'vue'

import { MultiSelect, ModelSelect } from "vue-search-select"

import { usePairingStore } from '@/stores/PairingStore.js'
import { useParticipateStore } from '@/stores/ParticipateStore.js'
import { useSettingsStore } from '@/stores/SettingsStore.js'

const pairingStore = usePairingStore()
const participateStore = useParticipateStore()
const settingsStore = useSettingsStore()

let pairingForm = reactive({
    participateIDs: [],
    pairingID: 'new'
})

let editPairingModalForm = reactive({
    pairing: null
})

function createUpdatePairing() {
    if (pairingForm.participateIDs === null) {
        pairingForm.participateIDs = []
        pairingForm.pairingID = 'new'
        return
    }

    pairingStore.assign(pairingForm.pairingID, pairingForm.participateIDs)

    pairingForm.participateIDs = []
    pairingForm.pairingID = 'new'
}

function editPairing(id) {
    editPairingModalForm.pairing = pairingStore.get(id)
}

function deletePairing(id) {
    pairingStore.remove(id)
}

function unassignParticipateFromPairing(pairingID, participateID) {
    pairingStore.unassign(pairingID, participateID)
    participateStore.setAssignedPairing(participateID, false)
}

const availableParticipates = computed(() => {
    let options = []

    let participates = [...participateStore.participates]
    participates.sort((a, b) => a.name.localeCompare(b.name))

    participates.forEach((participate) => {
        if (participate.assignedPairing == false) {
            options.push({value: participate.uuid, text: participate.name})
        }
    })

    return options
})

const availablePairings = computed(() => {
    let options = [{value: 'new', text: "Create New Pairing"}]

    pairingStore.pairings.forEach((pairing, i) => {
        options.push({value: pairing.uuid, text: pairingStore.getPairingTitle(pairing.uuid), disabled: pairing.participates.length >= settingsStore.tableSize})
    })

    return options
})

const listedPairings = computed(() => {
    return pairingStore.pairings
})

const selectingParticipateForPairingError = ref(false)
function selectingParticipateForPairing(items, lastSelectItem) {
    selectingParticipateForPairingError.value = items.length > settingsStore.tableSize
    pairingForm.participateIDs = items
}
</script>

<template>
    <div class="col card mb-4">
        <div class="card-header">
            <strong>Preferred Pairings</strong>
        </div>
        <div class="card-body">
            <div class="vstack gap-3 mx-auto">
                <multi-select
                    id="pairingParticipateSelection"
                    class="form-select"
                    :options="availableParticipates"
                    :selected-options="pairingForm.participateIDs"
                    placeholder="Find Participate(s)"
                    :is-error="selectingParticipateForPairingError"
                    @select="selectingParticipateForPairing"
                />

                <model-select
                    id="pairingSelection"
                    class="form-select"
                    :options="availablePairings"
                    v-model="pairingForm.pairingID"
                    placeholder="Find Pairing"
                />

                <button class="btn btn-success w-100" type="button" id="participateName" @click="createUpdatePairing">Submit</button>

                <details>
                    <summary>Pairings <span class="badge text-bg-secondary">{{ pairingStore.pairings.length }}</span></summary>
                    <table class="table" v-if="pairingStore.pairings.length > 0">
                        <tbody>
                            <tr v-for="pairing in listedPairings">
                                <td class="p-0 py-2 col-6">
                                    <div class="btn-group" style="float: right">
                                        <button type="button" class="btn btn-primary btn-sm dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">Actions</button>
                                        <ul class="dropdown-menu dropdown-menu-end">
                                            <li><button class="dropdown-item" type="button" data-bs-toggle="modal" data-bs-target="#editPairingModal" @click="editPairing(pairing.uuid)">Edit</button></li>
                                            <li><button class="dropdown-item" type="button" @click="deletePairing(pairing.uuid)">Delete</button></li>
                                        </ul>
                                    </div>
                                    {{ pairingStore.getPairingTitle(pairing.uuid) }}<br />
                                    <small>
                                        {{ pairingStore.getPairingStatus(pairing.uuid).status }} <span v-if="pairingStore.getPairingStatus(pairing.uuid).errors.length > 0">&middot; {{ pairingStore.getPairingStatus(pairing.uuid).errors }}</span>
                                    </small>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </details>
            </div>
        </div>

        <!-- Edit Pairing Modal -->
        <div class="modal fade" id="editPairingModal" tabindex="-1" aria-labelledby="editPairingModalLabel" aria-hidden="true">
            <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h1 class="modal-title fs-5" id="editPairingModalLabel">Edit Pairing</h1>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <table v-if="editPairingModalForm.pairing" class="table table-hover">
                            <thead>
                                <tr>
                                    <th scope="col">Name</th>
                                    <th scope="col">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="participate in editPairingModalForm.pairing.participates">
                                    <td>{{ participateStore.get(participate).name }}</td>
                                    <td><button class="btn btn-danger" type="button" @click="unassignParticipateFromPairing(editPairingModalForm.pairing.uuid, participate)">Remove</button></td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
</style>
